import { isHotkeyPressed } from 'react-hotkeys-hook'

/** Physical modifier keys a pointer gesture can be bound to. */
export type ModifierKey = 'shift' | 'ctrl' | 'meta' | 'alt'

/**
 * Held modifiers for pointer gestures. Change a role here to retarget every
 * check, label, and test that uses it. `ctrl` and `meta` are both listed where
 * Windows/Linux and macOS should accept the same gesture.
 */
export const pointerModifiers = {
  /** Hold and drag to marquee-select. */
  marquee: ['ctrl', 'meta'] as const,
  /** Hold while clicking to add to or extend the current selection. */
  multiSelect: ['shift'] as const,
  /** Hold while clicking a list row to toggle it in the selection. */
  listToggle: ['ctrl', 'meta'] as const,
}

export type PointerModifierRole = keyof typeof pointerModifiers

export type PointerModifierKeyboardName = 'Shift' | 'Control' | 'Meta' | 'Alt'

const modifierEventFlag = {
  shift: 'shiftKey',
  ctrl: 'ctrlKey',
  meta: 'metaKey',
  alt: 'altKey',
} as const

const modifierLabel: Record<ModifierKey, string> = {
  shift: 'Shift',
  ctrl: 'Strg',
  meta: 'Meta',
  alt: 'Alt',
}

const modifierKeyboardName: Record<ModifierKey, PointerModifierKeyboardName> = {
  shift: 'Shift',
  ctrl: 'Control',
  meta: 'Meta',
  alt: 'Alt',
}

type ModifierEvent = {
  shiftKey?: boolean
  ctrlKey?: boolean
  metaKey?: boolean
  altKey?: boolean
}

/** True when the event has any key configured for the role. A missing event never matches. */
export const eventMatchesPointerModifier = (
  event: ModifierEvent | null | undefined,
  role: PointerModifierRole,
): boolean =>
  event != null && pointerModifiers[role].some(key => Boolean(event[modifierEventFlag[key]]))

/** True when any key configured for the role is currently held, with no event in hand. */
export const isPointerModifierPressed = (role: PointerModifierRole): boolean =>
  pointerModifiers[role].some(key => isHotkeyPressed(key))

/** German label already used in the UI, e.g. `Strg/Meta` or `Shift`. */
export const formatPointerModifier = (role: PointerModifierRole): string =>
  pointerModifiers[role].map(key => modifierLabel[key]).join('/')

/** Playwright `keyboard.down` name for the role's first key. */
export const pointerModifierKeyboardName = (
  role: PointerModifierRole,
): PointerModifierKeyboardName => modifierKeyboardName[pointerModifiers[role][0]]
